import { isTauri } from '@/tauri/utils';
import { logger } from '@/utils/logger/logger';
import { extractPluginError } from '@heidi/plugin-core';
import { type Region } from '@heidi/schema/ml-scribe';

/**
 * Metadata for a region-appropriate update returned by the Rust updater.
 * `null` means no update is offered on the resolved channel.
 */
export type RegionUpdateInfo = { version: string; body?: string };

/**
 * Thrown when an updater call rejects. `errorCode` is Rust's `plugin_error_code`
 * for a call that reached Rust, or the code `withGlobalErrorCode` classifies for
 * the global feed's `legacy` path; both sources are live.
 */
export class UpdaterBridgeError extends Error {
  constructor(
    message: string,
    readonly errorCode?: string
  ) {
    super(message);
    this.name = 'UpdaterBridgeError';
  }
}

async function invokeUpdaterCommand<T>(
  command: string,
  payload: Record<string, unknown>
): Promise<T> {
  if (!isTauri()) {
    throw new UpdaterBridgeError(
      'Updater bridge is only available inside the Tauri runtime'
    );
  }
  const { invoke } = await import('@tauri-apps/api/core');
  try {
    return await invoke<T>(command, payload);
  } catch (error) {
    const { message, errorCode } = extractPluginError(error);
    // Stays `debug`, and deliberately carries no `tag`: the calling model logs
    // this same rejection at `error` with the version and the user's intent
    // attached. Anchoring both would double every Datadog alert and split
    // triage across two signatures for one failure. The model's log is the
    // canonical one; this is breadcrumb detail for a local repro.
    logger.debug('[updater] command failed', { command, message, errorCode });
    throw new UpdaterBridgeError(message, errorCode);
  }
}

/**
 * The region a call is bound to, or `null` for the global feed. `null` is sent
 * explicitly rather than omitted so Rust always deserializes the argument as a
 * present `Option` rather than depending on how an absent key is treated.
 */
export type UpdaterRegion = Region | null;

/**
 * What a cached install did. `cached` is the route the native side actually
 * took, not what the renderer expected, and `prefetchMs` is present only on
 * that route, so it always names the download that produced these bytes.
 */
export type CachedInstallReport = {
  installed: boolean;
  cached: boolean;
  prefetchMs?: number;
};

/**
 * The route of an install that already completed, reportable once. `null` on
 * every ordinary launch: a route survives only when the install never
 * returned, and only for the version now running.
 */
export type CompletedInstallReport = {
  version: string;
  cached: boolean;
  prefetchMs?: number;
};

/**
 * Region-aware updater bridge. Forwards the validated `Region`; Rust binds the
 * channel from a closed map and rejects unknown ones, and `null` means the
 * global feed. Mirrors the chronicle / rpa bridge shape (raw `invoke`, no toast).
 */
export const updaterBridge = {
  checkUpdateForRegion(region: UpdaterRegion): Promise<RegionUpdateInfo | null> {
    return invokeUpdaterCommand<RegionUpdateInfo | null>('check_update_for_region', {
      region,
    });
  },

  /** Resolves true when an update was installed (caller should relaunch). */
  installUpdateForRegion(region: UpdaterRegion): Promise<boolean> {
    return invokeUpdaterCommand<boolean>('install_update_for_region', { region });
  },

  /**
   * Record `version` as the newest offer on the feed the caller's check
   * resolved. The state arrives on `updater://prefetch-state`, re-emitted for
   * every call; `userInitiated` is what allows a retry of a failed prefetch.
   */
  prefetchUpdateForRegion(
    region: UpdaterRegion,
    version: string,
    userInitiated: boolean
  ): Promise<void> {
    return invokeUpdaterCommand<void>('prefetch_update_for_region', {
      region,
      version,
      userInitiated,
    });
  },

  /**
   * Forget the recorded offer, because the channel offers nothing or the
   * version was refused. No manifest fetch and no gate read — it only drops a
   * record Rust would otherwise keep re-evaluating.
   */
  clearUpdateOffer(): Promise<void> {
    return invokeUpdaterCommand<void>('clear_update_offer', {});
  },

  /**
   * Install the prefetched bundle for `version`, reporting the route taken.
   * Rust falls back to a full download when the cache cannot be trusted, so
   * this is never worse than {@link updaterBridge.installUpdateForRegion}.
   */
  installCachedUpdateForRegion(
    region: UpdaterRegion,
    version: string
  ): Promise<CachedInstallReport> {
    return invokeUpdaterCommand<CachedInstallReport>('install_cached_update_for_region', {
      region,
      version,
    });
  },

  /** Take the route of an install that completed without returning. */
  takeCompletedUpdateInstall(): Promise<CompletedInstallReport | null> {
    return invokeUpdaterCommand<CompletedInstallReport | null>(
      'take_completed_update_install',
      {}
    );
  },
};
